/**
 * Needle Inspector Agent - Entry Point
 *
 * This script is designed to be injected into any web page with a Three.js scene.
 * It auto-detects Three.js, boots minimal inspector services, and exposes a
 * programmatic API at window.__NEEDLE_INSPECTOR__ for AI agents.
 *
 * Usage from Playwright:
 *   await page.addScriptTag({ url: 'https://inspector.needle.tools/agent.js' });
 *   await page.waitForFunction(() => window.__NEEDLE_INSPECTOR__?.ready);
 *   const tools = await page.evaluate(() => window.__NEEDLE_INSPECTOR__.getTools());
 */

import { setupThreeDetection, type ThreeDetectionHost } from "./detect.js";
import { startupAgent } from "./services.js";
import { createInspectorAPI, type NeedleInspectorAPI } from "./api.js";

// Start detection immediately — must happen before Three.js initializes
const host = setupThreeDetection();

// Create a placeholder API that agents can detect
const api: NeedleInspectorAPI = {
    ready: false,
    getTools: async () => [],
    callTool: async () => ({ error: "Inspector not ready yet. Wait for ready === true." }),
    getHierarchy: async () => [],
    getSelectedNodes: async () => [],
    selectNode: async () => ({ success: false, message: "Inspector not ready yet." }),
    searchNodes: async () => [],
    getProperties: async () => ({ error: "Inspector not ready yet." }),
    findProperties: async () => ({ error: "Inspector not ready yet." }),
    readProperty: async () => ({ error: "Inspector not ready yet." }),
};

// Add runtime help for agents
(api as any).usage = "IMPORTANT: All methods are async. You MUST use 'await'. Example: const tree = await api.getHierarchy()";
(api as any).docs = "https://inspector.needle.tools/agent.md";

window.__NEEDLE_INSPECTOR__ = api;

/**
 * Initialize the inspector once a Three.js scene is detected.
 */
async function initialize(host: ThreeDetectionHost) {
    try {
        // Boot core services
        const app = await startupAgent();

        // Register detected scenes/renderers with the project service
        if (app.project) {
            for (const renderer of host.renderers) {
                app.project.add(renderer);
            }
            for (const scene of host.scenes) {
                app.project.add(scene);
            }
        }

        // Listen for new scenes/renderers added after initialization
        host.onChanged(evt => {
            if (evt.type === "added" && app.project) {
                app.project.add(evt.object);
            }
        });

        // Create and expose the full API
        const fullApi = createInspectorAPI(app, app.analytics ?? undefined);
        fullApi.ready = true;

        // Track agent initialization
        app.analytics?.logEvent("agent.ready", { host: window.location.host });

        // Replace placeholder methods with real ones
        Object.assign(api, fullApi);

        console.log("[Needle Inspector Agent] Ready. Use window.__NEEDLE_INSPECTOR__ to inspect the scene.");
    } catch (err) {
        console.error("[Needle Inspector Agent] Failed to initialize:", err);
    }
}


// Wait for Three.js scenes to appear, then initialize
let initialized = false;

function tryInitialize() {
    if (initialized) return;
    if (host.scenes.length > 0 || host.renderers.length > 0) {
        initialized = true;
        initialize(host);
    }
}

// Check if scenes are already detected (script loaded after Three.js)
tryInitialize();

// Also listen for future scene additions
host.onChanged(() => tryInitialize());

// Fallback: keep checking for a while in case Three.js loads lazily
const checkInterval = setInterval(() => {
    tryInitialize();
    if (initialized) {
        clearInterval(checkInterval);
    }
}, 500);

// Stop checking after 30 seconds
setTimeout(() => {
    clearInterval(checkInterval);
    if (!initialized) {
        console.warn("[Needle Inspector Agent] No Three.js scene detected after 30 seconds. The inspector will activate if a scene is added later.");
    }
}, 30_000);
